import { useState, useRef, useCallback, useEffect } from 'react';
import { polaroidRepository } from '../../data/polaroidRepository';
import { MobileSheet } from '@/shared/components/MobileSheet';

type Fase = 'idle' | 'cooldown' | 'preview' | 'enviando' | 'sucesso' | 'erro';

const COOLDOWN_MS = 5 * 60 * 1_000; // 5 minutos

function cooldownKey(telaId: string) { return `ds:polaroid_cooldown_${telaId}`; }

function cooldownRestante(telaId: string): number {
  const val = localStorage.getItem(cooldownKey(telaId));
  if (!val) return 0;
  const expira = parseInt(val, 10);
  return Math.max(0, expira - Date.now());
}

function formatarCooldown(ms: number): string {
  const s = Math.ceil(ms / 1_000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}:${String(r).padStart(2, '0')}` : `${s}s`;
}

// Redimensiona para max 900×900, JPEG 0.78 — ~130-220 kB base64 no banco
function redimensionar(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const MAX = 900;
      let w = img.width, h = img.height;
      if (w > MAX || h > MAX) {
        if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
        else        { w = Math.round(w * MAX / h); h = MAX; }
      }
      const canvas = document.createElement('canvas');
      canvas.width  = w;
      canvas.height = h;
      canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', 0.78));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject();
    };
    img.src = url;
  });
}

interface Props {
  telaId:           string;
  empresaId:        string;
  sessionId:        string;
  /** Abre o seletor/câmera assim que montar (ex.: atalho do menu +). */
  autoOpenCamera?:  boolean;
  onClose?:         () => void;
}

export function PolaroidCapture({
  telaId,
  empresaId,
  sessionId,
  autoOpenCamera = false,
  onClose,
}: Props) {
  const [fase,          setFase]          = useState<Fase>(() =>
    cooldownRestante(telaId) > 0 ? 'cooldown' : 'idle',
  );
  const [previewUrl,    setPreviewUrl]    = useState<string | null>(null);
  const [identificacao, setIdentificacao] = useState('');
  const [erroMsg,       setErroMsg]       = useState('');
  const [tempoRestante, setTempoRestante] = useState(() => cooldownRestante(telaId));

  const fileRef    = useRef<HTMLInputElement>(null);
  const fileObjRef = useRef<File | null>(null);
  const autoOpenedRef = useRef(false);
  const sucessoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  useEffect(() => () => {
    if (sucessoTimerRef.current) clearTimeout(sucessoTimerRef.current);
  }, []);

  // Menu +: dispara câmera/galeria imediatamente, sem botão intermediário
  useEffect(() => {
    if (!autoOpenCamera || fase !== 'idle' || autoOpenedRef.current) return;
    autoOpenedRef.current = true;

    let input: HTMLInputElement | null = null;
    let focusTimer: number | null = null;
    const onWindowFocus = () => {
      focusTimer = window.setTimeout(() => {
        if (!fileObjRef.current && !input?.files?.length) {
          onClose?.();
        }
      }, 300);
    };
    const timer = window.setTimeout(() => {
      input = fileRef.current;
      if (!input) return;
      input.click();
      window.addEventListener('focus', onWindowFocus, { once: true });
    }, 50);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('focus', onWindowFocus);
      if (focusTimer) window.clearTimeout(focusTimer);
    };
  }, [autoOpenCamera, fase, onClose]);

  // Tick de 1s para atualizar o contador de cooldown
  useEffect(() => {
    if (fase !== 'cooldown') return;
    const interval = setInterval(() => {
      const restante = cooldownRestante(telaId);
      setTempoRestante(restante);
      if (restante <= 0) {
        setFase('idle');
        clearInterval(interval);
      }
    }, 1_000);
    return () => clearInterval(interval);
  }, [fase, telaId]);

  const abrirCamera = useCallback(() => {
    setFase('idle');
    setPreviewUrl(null);
    setIdentificacao('');
    setErroMsg('');
    fileRef.current?.click();
  }, []);

  const onFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    fileObjRef.current = file;
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    setFase('preview');
    e.target.value = '';
  }, []);

  const cancelar = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    fileObjRef.current = null;
    if (autoOpenCamera) {
      onClose?.();
      return;
    }
    setFase(cooldownRestante(telaId) > 0 ? 'cooldown' : 'idle');
  }, [previewUrl, telaId, autoOpenCamera, onClose]);

  const enviar = useCallback(async () => {
    const file = fileObjRef.current;
    if (!file) return;

    setFase('enviando');
    try {
      const photoData = await redimensionar(file);

      await polaroidRepository.inserir({
        tela_id:       telaId,
        empresa_id:    empresaId,
        session_id:    sessionId,
        identificacao: identificacao.trim() || null,
        photo_data:    photoData,
      });

      // Registra cooldown
      localStorage.setItem(cooldownKey(telaId), String(Date.now() + COOLDOWN_MS));
      const restante = cooldownRestante(telaId);
      setTempoRestante(restante);

      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      fileObjRef.current = null;
      setFase('sucesso');

      // Após 3s vai para cooldown (ou fecha se veio do menu +)
      sucessoTimerRef.current = setTimeout(() => {
        if (autoOpenCamera) {
          onClose?.();
        } else {
          setFase('cooldown');
        }
      }, 3_000);
    } catch {
      setErroMsg('Falha no envio. Tente novamente.');
      setFase('erro');
    }
  }, [telaId, empresaId, sessionId, identificacao, previewUrl, autoOpenCamera, onClose]);

  // ── Cooldown: botão bloqueado com countdown ─────────────────────────────────
  if (fase === 'cooldown') {
    return (
      <button
        disabled
        aria-label="Aguarde para enviar outra foto"
        className="
          fixed bottom-20 right-4 z-30
          flex items-center gap-2 pl-3.5 pr-4 py-3 rounded-full
          bg-white/[0.08] border border-white/[0.10]
          text-white/40 text-sm font-semibold cursor-not-allowed
        "
      >
        <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        {formatarCooldown(tempoRestante)}
      </button>
    );
  }

  // ── Idle: botão câmera (ou só input oculto quando autoOpenCamera) ───────────
  if (fase === 'idle') {
    return (
      <>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="user"
          className="hidden"
          onChange={onFileChange}
          aria-hidden
        />
        {!autoOpenCamera && (
          <button
            onClick={abrirCamera}
            aria-label="Mandar foto para a TV"
            className="
              fixed bottom-20 right-4 z-30
              flex items-center gap-2 pl-3.5 pr-4 py-3 rounded-full
              bg-gradient-to-r from-violet-500 to-purple-600
              text-white text-sm font-bold shadow-2xl shadow-violet-500/40
              active:scale-95 transition-all
            "
          >
            <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Foto na TV
          </button>
        )}
      </>
    );
  }

  // ── Sucesso ─────────────────────────────────────────────────────────────────
  if (fase === 'sucesso') {
    return (
      <div className="fixed bottom-20 right-4 z-30 max-w-[240px] rounded-2xl bg-emerald-500/15 border border-emerald-500/25 px-4 py-3 animate-slide-up">
        <div className="flex items-start gap-2.5">
          <svg className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          <p className="text-emerald-300 text-sm font-semibold leading-snug">
            Foto enviada! Aguarde a liberação do garçom para aparecer na TV.
          </p>
        </div>
      </div>
    );
  }

  // ── Preview / Enviando / Erro ────────────────────────────────────────────────
  return (
    <MobileSheet aberto titulo="Foto na TV" onClose={fase === 'enviando' ? () => {} : cancelar}>
      <div className="flex items-center justify-between px-5 pb-3 shrink-0">
        <h3 className="text-white font-bold text-base tracking-tight">Foto na TV 📸</h3>
        <button
          onClick={cancelar}
          disabled={fase === 'enviando'}
          aria-label="Fechar"
          className="w-11 h-11 rounded-full bg-white/[0.06] flex items-center justify-center text-white/40 hover:text-white/70 transition-colors disabled:opacity-30"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="px-5 pb-8 space-y-4 flex-1 min-h-0 overflow-y-auto">
        {previewUrl && (
          <div className="w-full aspect-[4/3] rounded-2xl overflow-hidden bg-white/[0.04] border border-white/[0.06]">
            <img src={previewUrl} alt="Prévia da foto" className="w-full h-full object-cover" />
          </div>
        )}

        <div>
          <label htmlFor="polaroid-id" className="block text-white/40 text-xs font-semibold uppercase tracking-wider mb-1.5">
            Seu nome ou mesa (opcional)
          </label>
          <input
            id="polaroid-id"
            type="text"
            value={identificacao}
            onChange={e => setIdentificacao(e.target.value)}
            placeholder="Ex: Mesa 04, João & Maria..."
            maxLength={60}
            disabled={fase === 'enviando'}
            className="
              w-full bg-white/[0.04] border border-white/[0.06] rounded-2xl
              px-4 py-3 text-sm text-white placeholder-white/20
              focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/50
              focus:border-violet-500/50 focus:bg-white/[0.06] transition-all disabled:opacity-40
            "
          />
        </div>

        {fase === 'erro' && erroMsg && (
          <p className="text-red-400 text-sm font-semibold">{erroMsg}</p>
        )}

        <div className="flex gap-3">
          <button
            onClick={cancelar}
            disabled={fase === 'enviando'}
            className="flex-1 py-3.5 rounded-2xl bg-white/[0.06] text-white/60 text-sm font-semibold active:scale-[0.97] transition-all disabled:opacity-30"
          >
            Cancelar
          </button>
          {/* Erro: reenvia a mesma foto (fileObjRef e a prévia continuam). Abrir a
              câmera daqui não funcionava — o <input file> só existe na fase idle. */}
          <button
            onClick={enviar}
            disabled={fase === 'enviando'}
            className="
              flex-1 py-3.5 rounded-2xl text-white text-sm font-bold
              bg-gradient-to-r from-violet-500 to-purple-600
              shadow-lg shadow-violet-500/30
              active:scale-[0.97] transition-all disabled:opacity-50
              flex items-center justify-center gap-2
            "
          >
            {fase === 'enviando' ? (
              <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Enviando...</>
            ) : fase === 'erro' ? 'Tentar novamente' : 'Enviar para a TV'}
          </button>
        </div>
      </div>
    </MobileSheet>
  );
}
